import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTheme } from '../utils/theme-context';
import { useAuth } from '../hooks/useAuth';
import supabase from '../integrations/supabase';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import DarkModeToggle from './DarkModeToggle';

  const navLinks = [
    { to: '/dashboard', label: 'Base', icon: '🎓', enabled: true },
    { to: '/notes', label: 'Notes', icon: '📝', enabled: true },
    { to: '/planner', label: 'Plan', icon: '🎯', enabled: true },
    { to: '/calendar', label: 'Calendar', icon: '🗓️', enabled: true },
    { to: '/multiplayer', label: 'Multiplayer', icon: '⚔️', enabled: true },
    { to: '/reading', label: 'Reading', icon: '🎧', enabled: true },
    { to: '/habit-hub', label: 'Habits', icon: '🧘', enabled: true },
    { to: '/leaderboard', label: 'Leaderboard', icon: '🏅', enabled: true },
  ];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const menuRef = useRef();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user, logout } = useAuth();

  // Add logic to get class from database
  const [userClass, setUserClass] = useState('');
  useEffect(() => {
    const getUserClass = async () => {
      if (user) {
        try {
          // Get course selection from database
          const { data: courseSelection, error } = await supabase
            .from('course_selections')
            .select('selected_class')
            .eq('user_id', user.id)
            .single();
          
          if (courseSelection && !error) {
            setUserClass(courseSelection.selected_class);
          } else {
            // Fallback to localStorage if database query fails
            const stored = localStorage.getItem('user');
            if (stored) {
              try {
                const parsed = JSON.parse(stored);
                if (parsed.class) setUserClass(parsed.class);
              } catch {}
            }
          }
        } catch (error) {
          console.error('Error fetching user class:', error);
          // Fallback to localStorage
          const stored = localStorage.getItem('user');
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              if (parsed.class) setUserClass(parsed.class);
            } catch {}
          }
        }
      }
    };
    
    getUserClass();
  }, [user]);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);



  const handleLogout = async () => {
    try {
      console.log('🚪 Logging out user:', user?.email);
      
      // Close menu first
      setMenuOpen(false);
      
      // Use the auth hook logout
      await logout();
      
      console.log('✅ Logout successful');
      
      // Navigate to login with replace to prevent back navigation
      navigate('/login', { replace: true });
      
    } catch (err) {
      console.error('❌ Logout error:', err);
      // Even if logout fails, clear local state and redirect
      localStorage.removeItem('user_token');
      localStorage.removeItem('refresh_token');
      navigate('/login', { replace: true });
    }
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-2 py-2 w-full min-w-0 border-b bg-white dark:bg-black"
      style={{ background: theme.appHeaderBg, color: theme.appHeaderColor, borderColor: theme.borderColor, fontFamily: theme.fontFamily }}
    >
      <div className="flex items-center gap-3 min-w-0" style={{ color: theme.textColor }}>
        <img src={process.env.PUBLIC_URL + '/logos/knoowl-logo.svg'} alt="Knoowl Logo" style={{ height: 60, width: 'auto' }} />
      </div>




      



        {/* Hamburger menu for mobile */}
      <div className="flex items-center gap-3 relative min-w-0 md:hidden">
        <IconButton 
          onClick={() => setDrawerOpen(true)} 
          size="large" 
          aria-label="Open menu" 
          style={{ color: theme.textColor }}
          className="transition-all duration-200 ease-out hover:scale-105 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
        >
          <MenuIcon fontSize="inherit" />
        </IconButton>
      </div>
      {/* Desktop nav and profile */}
      <nav className="gap-2 md:gap-4 overflow-x-auto max-w-full flex-shrink min-w-0 hidden md:flex">
        {navLinks.map(link => (
          <div key={link.to} className="relative group">
            {link.enabled ? (
              <NavLink
                to={link.to}
                className={({ isActive }) =>
                `flex flex-col items-center px-2 py-1 font-bold text-xs md:text-base whitespace-nowrap border-b-2 transition-all duration-200 ease-out hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md ${isActive ? 'border-black dark:border-white' : 'border-transparent'}`
                }
                style={({ isActive }) => ({
                  color: theme.textColor,
                  borderBottomColor: isActive ? theme.textColor : 'transparent',
                })}
                >
                  <span className="text-lg md:text-xl transition-transform duration-200 ease-out hover:scale-105" aria-hidden="true">{link.icon}</span>
                  <span className="hidden md:inline transition-all duration-200">{link.label}</span>
                </NavLink>
            ) : (
              <button
                className="flex flex-col items-center px-2 py-1 font-bold text-xs md:text-base whitespace-nowrap border-b-2 border-transparent opacity-50 cursor-not-allowed transition-all duration-200 ease-out hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md"
                style={{ color: theme.textColor }}
                disabled
                title={link.comingSoon ? "Coming soon" : "Not available"}
              >
                <span className="text-lg md:text-xl transition-transform duration-200 ease-out hover:scale-105" aria-hidden="true">{link.icon}</span>
                <span className="hidden md:inline transition-all duration-200">{link.label}</span>
              </button>
            )}
            {link.comingSoon && (
              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                Coming soon
              </div>
            )}
          </div>
        ))}
      </nav>
      <div className="items-center gap-3 relative min-w-0 hidden md:flex" ref={menuRef}>
        {/* Dark Mode Toggle */}
        <DarkModeToggle />
        {/* Profile button and popover as before */}
        <button
          className="w-11 h-11 rounded-full flex items-center justify-center border-none focus:outline-none transition-all duration-200 ease-out hover:scale-105 hover:shadow-md"
          style={{ background: theme.cardBg }}
          onClick={() => setMenuOpen((open) => !open)}
          aria-label="Profile menu"
        >
          {user?.avatar_url ? (
            <img
              src={user.avatar_url}
              alt="Profile"
              className="w-9 h-9 rounded-full object-cover"
            />
          ) : (
          <img
            src="https://api.dicebear.com/7.x/thumbs/svg?seed=profile"
            alt="Profile"
            className="w-9 h-9 rounded-full object-cover"
          />
          )}
        </button>
          {menuOpen && (
          <div
            className="absolute right-0 top-14 z-50 w-56 rounded-2xl shadow-2xl py-2 flex flex-col"
            style={{ background: theme.cardBg, color: theme.cardText, border: `1px solid ${theme.borderColor}` }}
          >
            {/* User Info */}
            {user && (
              <div className="px-5 py-3 border-b" style={{ borderColor: theme.borderColor }}>
                <div className="font-semibold text-sm" style={{ color: theme.cardText }}>
                  {user.full_name || user.user_metadata?.full_name || user.email}
                </div>
                <div className="text-xs" style={{ color: theme.cardText, opacity: 0.7 }}>
                  {user.email}
                </div>
                {userClass && (
                  <div className="text-xs mt-1" style={{ color: theme.cardText, opacity: 0.7 }}>Class {userClass}</div>
                )}
              </div>
            )}
              <button
              className="text-left px-5 py-3 font-semibold text-sm transition-all duration-200 ease-out hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
              style={{ color: theme.cardText, background: 'transparent' }}
                onClick={() => { setMenuOpen(false); navigate('/my-progress'); }}
              >
              📊 My Progress
              </button>
              <button
              className="text-left px-5 py-3 font-semibold text-sm transition-all duration-200 ease-out hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
              style={{ color: theme.cardText, background: 'transparent' }}
                onClick={() => { setMenuOpen(false); navigate('/settings'); }}
              >
              ⚙️ Settings
              </button>
              <button
              className="text-left px-5 py-3 font-semibold text-sm transition-all duration-200 ease-out hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
              style={{ color: '#e11d48', background: 'transparent' }}
                onClick={handleLogout}
              >
              🚪 Logout
              </button>
          </div>
          )}
      </div>
      {/* Drawer for mobile nav */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <div className="w-64 flex flex-col h-full bg-white dark:bg-black" style={{ minHeight: '100vh' }}>
          <div className="flex items-center gap-2 p-4 border-b border-gray-200 dark:border-gray-800">
            <img src={process.env.PUBLIC_URL + '/logos/knoowl-logo.svg'} alt="Knoowl Logo" style={{ height: 60, width: 'auto' }} />
          </div>
          <nav className="flex flex-col gap-1 p-4">
            {navLinks.map(link => (
              <div key={link.to} className="relative">
                {link.enabled ? (
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded text-base font-bold transition-all duration-200 ease-out hover:bg-gray-100 dark:hover:bg-gray-800 ${isActive ? 'bg-gray-100 dark:bg-gray-800' : ''}`
                    }
                    style={{ color: theme.textColor }}
                    onClick={() => setDrawerOpen(false)}
                  >
                    <span className="text-xl transition-transform duration-200 ease-out hover:scale-105">{link.icon}</span>
                    <span className="transition-all duration-200">{link.label}</span>
                  </NavLink>
                ) : (
                  <button
                    className="flex items-center gap-3 px-3 py-2 rounded text-base font-bold opacity-50 cursor-not-allowed w-full text-left transition-all duration-200 ease-out hover:bg-gray-100 dark:hover:bg-gray-800"
                    style={{ color: theme.textColor }}
                    disabled
                    title={link.comingSoon ? "Coming soon" : "Not available"}
                  >
                    <span className="text-xl transition-transform duration-200 ease-out hover:scale-105">{link.icon}</span>
                    <span className="transition-all duration-200">{link.label}</span>
                    {link.comingSoon && (
                      <span className="ml-auto text-xs bg-gray-600 text-white px-2 py-1 rounded">
                        Coming soon
                      </span>
                    )}
                  </button>
                )}
              </div>
            ))}
          </nav>
          <div className="flex-1" />
          <div className="p-4 border-t border-gray-200 dark:border-gray-800 flex flex-col gap-2">
            {/* Profile/settings/logout (reuse profile popover content here) */}
            {/* ... profile info and logout/settings buttons ... */}
            {user && (
              <div className="px-3 py-2 rounded text-base font-bold" style={{ color: theme.textColor }}>
                <div className="font-semibold text-sm" style={{ color: theme.cardText }}>
                  {user.full_name || user.user_metadata?.full_name || user.email}
                </div>
                <div className="text-xs" style={{ color: theme.cardText, opacity: 0.7 }}>
                  {user.email}
                </div>
                {userClass && (
                  <div className="text-xs" style={{ color: theme.cardText, opacity: 0.7 }}>Class {userClass}</div>
                )}
              </div>
            )}
            <button
              className="text-left px-3 py-2 rounded text-base font-bold transition-all duration-200 ease-out hover:bg-gray-100 dark:hover:bg-gray-800"
              style={{ color: theme.cardText, background: 'transparent' }}
              onClick={() => { setDrawerOpen(false); navigate('/my-progress'); }}
            >
              📊 My Progress
            </button>
            <button
              className="text-left px-3 py-2 rounded text-base font-bold transition-all duration-200 ease-out hover:bg-gray-100 dark:hover:bg-gray-800"
              style={{ color: theme.cardText, background: 'transparent' }}
              onClick={() => { setDrawerOpen(false); navigate('/settings'); }}
            >
              ⚙️ Settings
            </button>
            <button
              className="text-left px-3 py-2 rounded text-base font-bold transition-all duration-200 ease-out hover:bg-gray-100 dark:hover:bg-gray-800"
              style={{ color: '#e11d48', background: 'transparent' }}
              onClick={handleLogout}
            >
              🚪 Logout
            </button>
          </div>
        </div>
      </Drawer>
    </header>
  );
};

export default Header; 